'use client';

import React, {
  forwardRef,
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  Children,
  isValidElement,
  cloneElement,
} from 'react';
import {
  tabVariants,
  tabDisabledClasses,
  lineContainerClasses,
  boxTablistClasses,
  boxTrayClasses,
} from './Tab.style';
import { TabProps, TabsProps, TabType, TabSize, TabContextValue } from './Tab.types';
import { Link } from '@/lib/app-router';
import { inlineCenter, cn } from '@/lib/styleUtils';

const TabContext = createContext<TabContextValue | null>(null);

const useTabContext = () => {
  return useContext(TabContext);
};

const defaultIndicatorRect = {
  left: 0,
  width: 0,
  top: 0,
  height: 0,
};

export const Tabs: React.FC<TabsProps> = ({
  type = TabType.line,
  size = TabSize.medium,
  defaultActiveIndex = 0,
  activeIndex: controlledActiveIndex,
  onTabChange,
  children,
  className,
  ...rest
}) => {
  const [internalActiveIndex, setInternalActiveIndex] = useState(defaultActiveIndex);
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorRect, setIndicatorRect] = useState(defaultIndicatorRect);

  const isControlled = controlledActiveIndex !== undefined;
  const activeIndex = isControlled ? controlledActiveIndex : internalActiveIndex;

  const containerClasses = type === TabType.line ? lineContainerClasses : boxTablistClasses;

  // Adding or removing a tab changes neither activeIndex nor the container width.
  const tabCount = Children.toArray(children).filter(isValidElement).length;

  const updateIndicator = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const tabs = container.querySelectorAll<HTMLElement>('[role="tab"]');
    const activeTab = tabs[activeIndex];
    if (!activeTab) return;
    const tabRect = activeTab.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    setIndicatorRect({
      left: tabRect.left - containerRect.left,
      width: tabRect.width,
      top: tabRect.top - containerRect.top,
      height: tabRect.height,
    });
  }, [activeIndex]);

  useLayoutEffect(() => {
    updateIndicator();
  }, [updateIndicator, tabCount]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(updateIndicator);
    observer.observe(container);
    return () => observer.disconnect();
  }, [updateIndicator]);

  const handleTabChange = (index: number) => {
    if (!isControlled) {
      setInternalActiveIndex(index);
    }
    onTabChange?.(index);
  };

  const showIndicator = indicatorRect.width > 0;

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) return;

    const tabs = Array.from(container.querySelectorAll<HTMLElement>('[role="tab"]'));
    const currentIndex = tabs.findIndex((tab) => tab === document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex: number | null = null;

    switch (e.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    e.preventDefault();
    tabs[nextIndex].focus();
  }, []);

  return (
    <TabContext.Provider value={{ type, size, activeIndex, setActiveIndex: handleTabChange }}>
      <div
        ref={containerRef}
        className={cn('tw:relative tw:w-full', type === TabType.box && boxTrayClasses, className)}
        {...rest}
      >
        <div
          role="tablist"
          className={cn(containerClasses, type === TabType.box && 'tw:relative tw:z-10')}
          onKeyDown={handleKeyDown}
        >
          {(() => {
            // Assign indices counting only valid Tab elements. A conditionally
            // rendered tab (e.g. `{cond && <Tab/>}`) that evaluates to `false`
            // must NOT consume an index — otherwise the cloned tab indices drift
            // ahead of the rendered tab order, putting the active-label highlight
            // one tab to the left of the underline/content (PB-798). The DOM
            // indicator already counts only `[role="tab"]` nodes, so both must
            // agree on a falsy-free index sequence.
            let tabIndex = 0;
            return Children.map(children, (child) => {
              if (isValidElement<TabProps>(child)) {
                return cloneElement(child, { index: tabIndex++ });
              }
              return child;
            });
          })()}
        </div>
        {type === TabType.line && (
          <span
            aria-hidden
            className="tw:pointer-events-none tw:absolute tw:bottom-0 tw:h-0.5 tw:bg-primary tw:transition-[left,width] tw:duration-200 tw:ease-out"
            // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (sliding tab indicator position/width)
            style={{
              left: indicatorRect.left,
              width: showIndicator ? indicatorRect.width : 0,
            }}
          />
        )}
        {type === TabType.box && (
          <span
            aria-hidden
            className="tw:pointer-events-none tw:absolute tw:z-0 tw:top-nano tw:bottom-nano tw:rounded-8 tw:bg-white tw:shadow-[0px_1px_3px_0px_rgba(76,89,107,0.3)] tw:transition-[left,width] tw:duration-200 tw:ease-out"
            // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (sliding tab indicator position/width)
            style={{
              left: indicatorRect.left,
              width: showIndicator ? indicatorRect.width : 0,
            }}
          />
        )}
      </div>
    </TabContext.Provider>
  );
};

export const Tab = forwardRef<HTMLElement, TabProps>(
  (
    { children, index, leftIcon, topIcon, rightIcon, className, href, 'data-cy': dataCy, ...rest },
    ref
  ) => {
    const context = useTabContext();
    const tabType = context?.type ?? TabType.line;
    const size = context?.size ?? TabSize.medium;
    const hasTopIcon = !!topIcon;

    const isActive = index !== undefined && context?.activeIndex === index;

    const sharedClassName = cn(
      tabVariants({ tabType, size, isActive, hasTopIcon }),
      tabDisabledClasses,
      className
    );

    const iconContent = (
      <>
        {topIcon && (
          <span className={inlineCenter} aria-hidden="true">
            {topIcon}
          </span>
        )}
        {leftIcon && (
          <span className={inlineCenter} aria-hidden="true">
            {leftIcon}
          </span>
        )}
        {children}
        {rightIcon && (
          <span className={inlineCenter} aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </>
    );

    if (href) {
      const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (index !== undefined && context?.setActiveIndex) {
          context.setActiveIndex(index);
        }
      };

      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          role="tab"
          tabIndex={isActive ? 0 : -1}
          className={sharedClassName}
          aria-selected={isActive}
          onClick={handleLinkClick}
          data-cy={dataCy}
        >
          {iconContent}
        </Link>
      );
    }

    const { onClick, ...buttonRest } = rest as Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      'className' | 'children'
    >;

    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (index !== undefined && context?.setActiveIndex) {
        context.setActiveIndex(index);
      }
      onClick?.(e);
    };

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        role="tab"
        tabIndex={isActive ? 0 : -1}
        className={sharedClassName}
        aria-selected={isActive}
        onClick={handleButtonClick}
        data-cy={dataCy}
        {...buttonRest}
      >
        {iconContent}
      </button>
    );
  }
);

Tab.displayName = 'Tab';
